import {
  Component,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { AuthService } from '../../../authentication/data-access/services/auth.service';
import { RouteEnum } from '../../enums/routes';
import { ImagePaths } from '../../../admin/enums/imagePaths';
import { MatSidenav } from '@angular/material/sidenav';
import { Roles } from '../../../authentication/data-access/models/auth.model';
import { UserStore } from '../../data-access/stores/user.store';
import { Subject, takeUntil } from 'rxjs';
import { NavSection } from '../../data-access/models/sideNavLink.model';

@Component({
    selector: 'app-side-nav-menu',
    templateUrl: './side-nav-menu.component.html',
    styleUrls: ['./side-nav-menu.component.scss', '../../utils/styling/sidenav.scss'],
    standalone: false
})
export class SideNavMenuComponent implements OnInit  {
  url: string = '';
  opened!: boolean;
  collapsed = signal(true);
  readonly ImagePaths = ImagePaths;
  @ViewChild('sidenav') sidenav!: MatSidenav;
  rolesEnum: typeof Roles = Roles;
  isUserRoleManager = false;
  darkNavBarRoutes = [
    '/',
    '/unauthorised',
    '/forbidden',
    '/not-found',
  ];
  baseLinks: { name: string; route: RouteEnum; }[] = [{name: '', route: RouteEnum.UniversityProfiles}];
  bursariesAndApplications: NavSection[] = [];
  studentManagement: NavSection[] = [];
  profileManagement: NavSection[] = [];
  reporting: NavSection[] = [];
  funding: NavSection[] = [];
  events: NavSection[] = [];


  constructor(
      private router: Router,
      private userStore: UserStore,
      private authService: AuthService,
    ) {
        this.router.events.forEach((event) => {
          if(event instanceof NavigationStart) {
            this.sidenav.close();
          }
        });
    }

  async ngOnInit(): Promise<void> {
    this.url = this.router.url;

    const logo = new Image();
    logo.src = ImagePaths.BBD_LOGO;

    const lightLogo = new Image();
    lightLogo.src = ImagePaths.BBD_LOGO_LIGHT;

    this.isUserRoleManager = this.userStore.hasRole([this.rolesEnum.USER_ROLE_MANAGER]);
    this.bursariesAndApplications = [
      {
        links: [
          { name: 'Ukukhula bursaries', route: RouteEnum.AdminDashboard, queryParams: { viewType: 'Ukukhula Bursary Fund' } },
          { name: 'BBD bursaries', route: RouteEnum.AdminDashboard, queryParams: { viewType: 'BBD Bursary Fund' } },
          { name: 'Active bursaries', route: RouteEnum.AdminActiveBursaries },
          { name: 'Applications', route: RouteEnum.AdminApplications },
          { name: 'Failed applications and bursaries', route: RouteEnum.AdminDeclinedApplications },
        ],
      },
    ];

    this.studentManagement = [
      {
        links: [
          { name: 'Nudge students', route: RouteEnum.AdminNudgeStudents }, 
          { name: "Onboard students", route: RouteEnum.OnboardStudents },
          { name: "Bulk upload students", route: RouteEnum.BulkUploadStudents }
        ],
      },
    ];

    this.funding = [
      {
        links: [{ name: 'Fund allocation', route: RouteEnum.AdminFundAllocations }],
      },
    ];

    this.reporting = [
      {
        links: [
          { name: 'Reports', route: RouteEnum.Reports },
        ],
      },
    ];

    const baseProfileManagementLinks = [
      { name: 'University profiles', route: RouteEnum.UniversityProfiles },
      { name: 'Student profiles', route: RouteEnum.StudentProfiles },
    ];

    const conditionalProfileManagementLinks = [
      {
        condition: this.isUserRoleManager,
        link: { name: 'Admin profiles', route: RouteEnum.AdminProfiles }
      },
    ];
    this.profileManagement = this.addConditionalLinks(baseProfileManagementLinks, conditionalProfileManagementLinks);

    this.events = [
      {
        links: [
          { name: 'Create a new event', route: RouteEnum.AdminCreateEvent },
          { name: 'Events', route: RouteEnum.AdminEventList}
        ]
      }
    ]

  }


  darkNavBar(): boolean {
    if (
      this.darkNavBarRoutes.includes(this.router.url) ||
      this.router.url.includes('/maintenance')
    ) {
      return true;
    }
    return false;
  }

  successScreenRoute(): boolean {
    if (
      this.router.url.includes('legal/agreements/privacy-policy') ||
      this.router.url.includes('legal/agreements/terms-conditions') ||
      this.router.url.includes('/unauthorised') ||
      this.router.url.includes('/forbidden') ||
      this.router.url.includes('/maintenance') ||
      this.router.url.includes('/not-found')
    ) {
      return true;
    }
    return false;
  }

  navigate(route: string) {
    this.router.navigate([route]);
  }

  addConditionalLinks(
    baseLinks: { name: string; route: RouteEnum }[], 
    conditionalLinks: {condition: boolean; link: { name: string; route: RouteEnum }}[]
  ) {
    const conditional = conditionalLinks.filter(item => item.condition).map(item => item.link);
    return [{ links: [...conditional, ...baseLinks] }];
  }

  logout(): void {
    this.authService.logout();
  }

  toggleSideNavIndicator() {
    this.collapsed.set(true);
  }
}
